import type { MockedCommonsNorthboundApiDependenciesType } from '../../interface/getMockedNorthboundAPIs/types';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import type { CriterionType as CriterionInputType } from '../../interface/v1/Criterion/types';
import type { LoggerInputType } from '../../services/loggerService/types';
import type { OptimizelyInputType } from '../../interface/v1/optimizely/types';
import type { AnalyticsServiceInputType } from '../../services/AnalyticsService/types';
import { ApplicationContextEnum } from '../../services/applicationService/enums';
import { BackgroundTaskRegistrar } from '../../services/backgroundTaskService/IBackgroundTaskManagerService';
import type { BreadcrumbInputType } from '../../services/breadcrumbService/types';
import type { ConsentServiceInputType } from '../../services/consentService/ConsentService';
import type { LaunchDarklyProjectType } from '../../services/featureFlagService';
import type { GrantHistoryInputType } from '../../services/grants/grantsHistory/types';
import type { GrantsInputType } from '../../services/grants/types';
import {
  GraphQLPlatform,
  GraphQLStack
} from '../../services/graphQLService/enums';
import type {
  IdleLogoutInputType,
  UserActivityInputType
} from '../../services/IdleService/types';
import type { LayoutInputType } from '../../services/LayoutsService/types';
import type {
  LocalizationServiceInputType,
  URLServiceInputType
} from '../../services/localizationService/types';
import type {
  SplunkRumInputType,
  DatadogInputType
} from '../../services/monitoringService/types';
import type { RouteServiceInputType } from '../../services/RoutesService';
import type { RoutesServiceDependenciesInputType } from '../../services/RoutesService/types';
import type { ServiceWorkerInputType } from '../../services/serviceWorkerService/input.types';
import type { LoginInputType } from '../../services/session/types';
import type { TenantHandlerParamsType } from '../../services/tenantHandler/strategy/external-types';
import type { TenantHandlerFilterType } from '../../services/tenantHandler/strategy/types';
import type { ForceTenantSelectionConfigType } from '../../services/tenantHandler/types';
import type { ThemeInputType } from '../../services/themeService/types';
import type { OnboardingInputType } from '../../services/userOnboardingService/types';
import type { ManifestLoaderType } from '../../services/mfeRouterService/transitionLoaderTypes';
import { Stack } from '../../core/types/stratus';
import { ClientsType } from '../clients/ClientsType';
import { RepositoriesType } from '../repositories/RepositoriesType';
import { ServicesType } from '../services/ServicesType';
import { ContainersType } from '../containers/ContainersInitializer';
import { EnvironmentVariablesInputType } from '../../services/environmentVariablesService/types';

export enum PlatformEnum {
  nativeOnly = 'native-only',
  auto = 'auto'
}

export type CommonsServicesDependenciesType = {
  onboarding: {
    onboardingDirectorServiceId: string;
  };
  stack: string;
};

export type SetServiceDependencies = {
  services: ServicesType;
  repositories: RepositoriesType;
  clients: ClientsType;
  containers?: ContainersType;
};

export type ABTestingInputType = {
  enabled: boolean;
  clientName?: string;
  snippetId?: string;
};

export type AstInputType = {
  enabled: boolean;
};

export type FeatureFlagsInputType = {
  enabled: boolean;
  projectSettingsList: LaunchDarklyProjectType[];
};

export type TenantHandlerInputType = {
  enabled: boolean;
  tenantHandlerList: TenantHandlerParamsType[];
  globalTenantHandler: string;
  tenantSelectorAssetReference: string;
  stack: Stack; // stack as ENUM
  isNative: boolean;
  tenantGroups?: Record<string, TenantHandlerFilterType>;
  forceTenantSelectionOnPublicRoutes: ForceTenantSelectionConfigType;
};

export type BackgroundTasksInputType = {
  enabled: boolean;
  backgroundTaskRegistrarList: BackgroundTaskRegistrar[];
};

export type NavigationServiceInputBlockNavigationPathListObjectType = {
  path: string;
  exact?: boolean;
  serviceId?: string;
  eventName?: string;
  properties?: Record<string, any>;
};

export type NavigationServiceInputType = {
  useLegacyNavigation?: boolean;
  removeLocaleUrl: any;
  useVirtualHistory?: boolean;
  blockNavigation: {
    pathList: (
      | string
      | NavigationServiceInputBlockNavigationPathListObjectType
    )[];
  };
  loader?: ManifestLoaderType;
};

export type EventServiceInputType = {
  removeSelfFilterByContainerInstanceId: boolean;
};

export type AdapterInputType = {
  disableRouter?: boolean;
};

export type AccessControlInputType = {
  enabled: boolean;
  actions?: string[];
  actionsPrefix?: string;
};

export type EntitlementsInputType = {
  enabled: boolean;
};

export type UserOnboardingInputType = {
  enable: boolean;
  path: string;
  forceUserOnboardingCriterionKey?: string;
  userOnboardingPath?: string;
};

export type OverridesInputType = {
  enable: boolean;
};

/**
 * Defines the feature flags specific to shell, which
 * are managed as part of the manifest.
 */
export type ShellFlagsInputType = {
  oaLooseLoad?: boolean;
  nativeRefreshListener?: boolean;
  nativeRefreshServicesOnAuthChange?: boolean;
  webUIRefreshOnAuthChange?: boolean;
  tenantSelectorSideMenuSkeleton?: boolean;
  /**
   * When TRUE, refreshes the auth token on initialization instead of reading it
   * from access-token cookies.
   */
  enableTokenRefreshOnInit?: boolean;
};

export type GraphQLServiceInputType = {
  mock?: boolean;
  platform?: GraphQLPlatform;
  isShellPortal?: boolean;
  serverUrl?: string;
  stack?: GraphQLStack;
  XApiKey?: string;
};

export type AuthProvidersPropsType = {
  initialAuthenticationProvider: AuthenticationProviderEnum;
  authenticationProvider: AuthenticationProviderEnum;
};

export type CommonsMockType = MockedCommonsNorthboundApiDependenciesType;

export type MonitoringInputType = {
  enabled: boolean;
  splunk?: SplunkRumInputType;
  datadog?: DatadogInputType;
};

export type CommonsInitializeType = {
  abTesting: ABTestingInputType;
  jWebPlatform: string;
  platform: PlatformEnum;
  analytics: AnalyticsServiceInputType;
  appName: string;
  authProviders: AuthProvidersPropsType;
  ast?: AstInputType;
  backgroundTasks?: BackgroundTasksInputType;
  basePath?: string;
  clientId: string;
  consent: ConsentServiceInputType;
  defaultCriterionKey?: string | false;
  defaultLayoutKey?: string;
  featureFlags?: FeatureFlagsInputType;
  grants?: GrantsInputType;
  grantsHistory?: GrantHistoryInputType;
  isNative: boolean;
  layouts?: LayoutInputType[];
  localization?: LocalizationServiceInputType;
  login: LoginInputType;
  logger?: LoggerInputType;
  monitoring?: MonitoringInputType;
  navigation?: NavigationServiceInputType;
  event: EventServiceInputType;
  routes?: RoutesServiceDependenciesInputType;
  serviceWorker: ServiceWorkerInputType;
  stack: Stack;
  shellFlags: ShellFlagsInputType;
  tenantHandlerProps?: TenantHandlerInputType;
  theme?: ThemeInputType;
  urlService: URLServiceInputType;
  userActivity: UserActivityInputType;
  idleLogout: IdleLogoutInputType;
  graphql: GraphQLServiceInputType;
  applicationContext: ApplicationContextEnum;
  onboarding: OnboardingInputType;
  mock: CommonsMockType;
  optimizely: OptimizelyInputType;
  breadcrumb: BreadcrumbInputType;
  criterions: CriterionInputType<any>[];
  entitlements: EntitlementsInputType;
  fallbackConfiguration: any;
  adapter: AdapterInputType;
  accessControl: AccessControlInputType;
  userOnboarding: UserOnboardingInputType;
  environmentVariables: EnvironmentVariablesInputType;
  overrides: OverridesInputType;
};

export {
  BreadcrumbInputType,
  ConsentServiceInputType,
  CriterionInputType,
  GrantHistoryInputType,
  GrantsInputType,
  LayoutInputType,
  LocalizationServiceInputType,
  LoggerInputType,
  LoginInputType,
  SplunkRumInputType,
  DatadogInputType,
  OnboardingInputType,
  OptimizelyInputType,
  RouteServiceInputType,
  RoutesServiceDependenciesInputType,
  ServiceWorkerInputType,
  Stack,
  ThemeInputType,
  URLServiceInputType,
  UserActivityInputType,
  IdleLogoutInputType
};
